'use client';

import { cn } from '@core/utils';
import { useSearchParams } from 'next/navigation';
import { useCallback, useRef, useState } from 'react';

import {
  usePageSearchBarVisibility,
  useSearchHistoryStore,
} from '@/app/_components/SearchBar';
import {
  DEFAULT_SEARCH_CATEGORY,
  isSearchCategory,
  SEARCH_CATEGORY_QUERY_KEY,
} from '@/app/_components/SearchBar/constants';
import { BREAKPOINTS } from '@/constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useAppBack } from '@/modules/wello-app/_hooks/useAppBack';

import { AllSearch } from './_components/AllSearch';
import { DonationBusinessSearch } from './_components/DonationBusinessSearch';
import { GiftSearch } from './_components/GiftSearch';
import { HometownNewsSearch } from './_components/HometownNewsSearch';
import { LocalMarketSearch } from './_components/LocalMarketSearch';
import { PolicyInfoSearch } from './_components/PolicyInfoSearch';
import { PolicySearch } from './_components/PolicySearch';
import { SearchPageHeader } from './_components/SearchPageHeader';
import { useIntegratedSearchFilter } from './_hooks/useIntegratedSearchFilter';
import { usePolicyFilter } from './_hooks/usePolicyFilter';
import { SECTION_HEADER_MAX_W } from './constants';

export default function SearchPage() {
  useAppBack();

  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  const searchParams = useSearchParams();
  const resultsSectionRef = useRef<HTMLDivElement>(null);
  const {
    state,
    setKeyword,
    toggleTargetCode,
    toggleProvisionCode,
    toggleJobCode,
    toggleAgeCode,
    resetFilters,
    setSort,
    setPage,
    activeFilterCount,
  } = usePolicyFilter();

  const {
    state: integratedState,
    setRegionCode,
    setSubRegionCode,
    togglePolicyInfoProvision,
    toggleGiftCategory,
    toggleGiftInstitution,
    toggleDonationInstitution,
    setDonationSort,
    resetHometownNewsFilters,
    resetPolicyInfoFilters,
    resetGiftFilters,
    resetDonationFilters,
    hometownNewsFilterCount,
    policyInfoFilterCount,
    giftFilterCount,
    donationFilterCount,
  } = useIntegratedSearchFilter();

  const searchHistory = useSearchHistoryStore((state) => state.data);
  const add = useSearchHistoryStore((state) => state.add);
  const remove = useSearchHistoryStore((state) => state.remove);
  const clear = useSearchHistoryStore((state) => state.clear);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { ref: searchBarRef } = usePageSearchBarVisibility();
  const categoryParam = searchParams.get(SEARCH_CATEGORY_QUERY_KEY);
  const category = isSearchCategory(categoryParam)
    ? categoryParam
    : DEFAULT_SEARCH_CATEGORY;
  const isAllCategory = category === 'all';
  const isPolicyCategory = category === 'policy';

  const handleSearch = (keyword: string) => {
    setKeyword(keyword);
    add(keyword);
  };

  function getIntegratedFilterCount() {
    switch (category) {
      case 'hometown-news':
        return hometownNewsFilterCount;
      case 'policy-info':
        return policyInfoFilterCount;
      case 'gifts':
        return giftFilterCount;
      case 'hometown-business':
        return donationFilterCount;
      default:
        return 0;
    }
  }

  const integratedFilterCount = getIntegratedFilterCount();

  const hasIntegratedFilter =
    category === 'hometown-news' ||
    category === 'policy-info' ||
    category === 'gifts' ||
    category === 'hometown-business';

  const filterCount = isAllCategory
    ? 0
    : isPolicyCategory
      ? activeFilterCount
      : integratedFilterCount;

  const onFilterClick =
    !isAllCategory && (isPolicyCategory || hasIntegratedFilter)
      ? () => setIsFilterOpen(true)
      : undefined;

  const scrollToResultsTop = useCallback(() => {
    const resultsSection = resultsSectionRef.current;
    if (!resultsSection) return;

    const top =
      resultsSection.getBoundingClientRect().top +
      window.scrollY -
      (isDesktopViewport ? 24 : 56);

    window.scrollTo({
      top: Math.max(top, 0),
      behavior: 'auto',
    });
  }, [isDesktopViewport]);

  const handlePageChange = useCallback(
    (page: number) => {
      setPage(page);
      scrollToResultsTop();
    },
    [scrollToResultsTop, setPage],
  );

  const closeFilter = useCallback(() => setIsFilterOpen(false), []);

  const renderSection = () => {
    switch (category) {
      case 'all':
        return <AllSearch keyword={state.keyword} />;
      case 'policy':
        return (
          <PolicySearch
            isFilterOpen={isFilterOpen}
            onCloseFilter={closeFilter}
            state={state}
            onToggleTargetCode={toggleTargetCode}
            onToggleProvisionCode={toggleProvisionCode}
            onToggleJobCode={toggleJobCode}
            onToggleAgeCode={toggleAgeCode}
            onResetFilters={resetFilters}
            onSortChange={setSort}
            onPageChange={handlePageChange}
          />
        );
      case 'hometown-news':
        return (
          <HometownNewsSearch
            keyword={state.keyword}
            page={state.page}
            onPageChange={handlePageChange}
            isFilterOpen={isFilterOpen}
            onCloseFilter={closeFilter}
            regionCode={integratedState.regionCode}
            subRegionCode={integratedState.subRegionCode}
            onRegionChange={setRegionCode}
            onSubRegionChange={setSubRegionCode}
            onReset={resetHometownNewsFilters}
          />
        );
      case 'policy-info':
        return (
          <PolicyInfoSearch
            keyword={state.keyword}
            page={state.page}
            onPageChange={handlePageChange}
            isFilterOpen={isFilterOpen}
            onCloseFilter={closeFilter}
            provisionCodes={integratedState.policyInfoProvisionCodes}
            onToggleProvision={togglePolicyInfoProvision}
            onReset={resetPolicyInfoFilters}
          />
        );
      case 'gifts':
        return (
          <GiftSearch
            keyword={state.keyword}
            page={state.page}
            onPageChange={handlePageChange}
            isFilterOpen={isFilterOpen}
            onCloseFilter={closeFilter}
            categoryIds={integratedState.giftCategoryIds}
            institutionIds={integratedState.giftInstitutionIds}
            onToggleCategory={toggleGiftCategory}
            onToggleInstitution={toggleGiftInstitution}
            onReset={resetGiftFilters}
          />
        );
      case 'hometown-business':
        return (
          <DonationBusinessSearch
            keyword={state.keyword}
            page={state.page}
            onPageChange={handlePageChange}
            isFilterOpen={isFilterOpen}
            onCloseFilter={closeFilter}
            institutionIds={integratedState.donationInstitutionIds}
            sort={integratedState.donationSort}
            onToggleInstitution={toggleDonationInstitution}
            onSortChange={setDonationSort}
            onReset={resetDonationFilters}
          />
        );
      case 'welmarket':
        return (
          <LocalMarketSearch
            keyword={state.keyword}
            page={state.page}
            onPageChange={handlePageChange}
          />
        );
    }
  };

  return (
    <div className="flex w-full flex-col items-center gap-12 p-40 mobile-tablet:gap-0 mobile-tablet:p-0">
      <div
        className={cn(
          'w-full max-w-screen-xl',
          SECTION_HEADER_MAX_W[category],
        )}
      >
        <SearchPageHeader
          ref={searchBarRef}
          keyword={state.keyword}
          filterCount={filterCount}
          recentKeywords={searchHistory ?? []}
          onSearch={handleSearch}
          onFilterClick={onFilterClick}
          onSelectRecent={handleSearch}
          onRemoveRecent={remove}
          onClearRecent={clear}
        />
      </div>

      <div ref={resultsSectionRef} className="w-full">
        {renderSection()}
      </div>
    </div>
  );
}
